/*
,------.                                    ,--.          ,--.      ,--------.    ,------.            ,--.           
|  .-.  \,--. ,--.,--,--,  ,--,--.,--,--,--.`--' ,---.    |  | ,---.'--.  .--'    |  .-.  \  ,--,--.,-'  '-. ,--,--. 
|  |  \  :\  '  / |      \' ,-.  ||        |,--.| .--'    |  || .-. |  |  |       |  |  \  :' ,-.  |'-.  .-'' ,-.  | 
|  '--'  / \   '  |  ||  |\ '-'  ||  |  |  ||  |\ `--.    |  |' '-' '  |  |       |  '--'  /\ '-'  |  |  |  \ '-'  | 
`-------'.-'  /   `--''--' `--`--'`--`--`--'`--' `---'    `--' `---'   `--'       `-------'  `--`--'  `--'   `--`--' 
         `---'                                                                                                      
*/
import { IdName } from '../../service/asset';
import { BaseService } from '../../service/base';

export interface RockingSegmentDTO {
    id: IdName;
    colour: string;
    geometry: google.maps.LatLng[];
    requiredKg: number;
    total: number;
    description: string;
    programs: string[]; // List of program ids that this segment is part of
}

export interface RockingProgramDTO {
    id: IdName;
    description: string;
    segments: RockingProgramSegmentDTO[];
    folders: RockingProgramSegmentDTO[];
    start: string;
    end: string;
    defaultKg: number;
}

export interface RockingProgramSegmentDTO {
    id: string;
    segmentId?: string;
    parentId: string;
    name?: string;
    total?: number;
    requiredKg?: number;
}

export class VisionRockingService extends BaseService {
    public cache?: Promise<RockingSegmentDTO[]>;
    public programCache?: Promise<RockingProgramDTO[]>;

    public async init() {
        return new Promise((resolve, _reject) => {
            console.log('Rocking Service Loaded');
            resolve('Loaded');
        });
    }

    public getPrograms(reload = false): Promise<RockingProgramDTO[]> {
        if (this.programCache && reload == false) {
            return this.programCache;
        } else {
            const promise = this.fetchPrograms();
            this.programCache = promise;
            return promise;
        }
    }

    private fetchPrograms(): Promise<RockingProgramDTO[]> {
        const url = this.url + '/api/' + this.orgid + '/rocking/program';
        return fetch(url, {
            method: 'GET',
            headers: this.headers(),
        }).then((response) => {
            return response.json();
        });
    }

    public cachedFetchData(): Promise<RockingSegmentDTO[]> {
        // Check if the data for the given id is already cached
        if (this.cache) {
            return this.cache;
        }

        // If not cached, fetch the data and store the promise in the cache
        const promise = this.fetchData();
        this.cache = promise;
        return promise;
    }

    private fetchData(): Promise<RockingSegmentDTO[]> {
        const url = this.url + '/api/' + this.orgid + '/rocking';
        return fetch(url, {
            method: 'GET',
            headers: this.headers(),
        }).then((response) => {
            return response.json();
        });
    }

    /** Delete a segment from the system.
     *
     */
    async deleteSegment(id: string) {
        const response = await this.delete<RockingSegmentDTO>(this.url + '/api/' + this.orgid + '/rocking/segment', id);
        this.cache = undefined;
        return response;
    }

    async saveSegment(data: RockingSegmentDTO) {
        if (data.id.id == undefined || data.id.id == '') {
            const response = await this.post<RockingSegmentDTO>(this.url + '/api/' + this.orgid + '/rocking/segment', data);
            return response;
        } else {
            const response = await this.put<RockingSegmentDTO>(this.url + '/api/' + this.orgid + '/rocking/segment', data);
            return response;
        }
    }

    async saveProgram(data: RockingProgramDTO) {
        if (data.id.id == undefined || data.id.id == '') {
            const response = await this.post<RockingProgramDTO>(this.url + '/api/' + this.orgid + '/rocking/program', data);
            return response;
        } else {
            const response = await this.put<RockingProgramDTO>(this.url + '/api/' + this.orgid + '/rocking/program', data);
            return response;
        }
    }

    /** Delete a rocking program from the system.
     *
     */
    async deleteProgram(programId: string) {
        const response = await this.delete<RockingProgramDTO>(this.url + '/api/' + this.orgid + '/rocking/program?id=' + programId, '');
        return response;
    }

    async updateProgramSegmentTotal(segmentId: string, total: number) {
        const response = await this.put(this.url + '/api/' + this.orgid + '/rocking/program/segment/total?segmentId=' + segmentId + '&totalKg=' + total, {});
        return response;
    }

    async updateProgramSegmentRequiredKg(segmentId: string, requiredKg: number) {
        const response = await this.put(this.url + '/api/' + this.orgid + '/rocking/program/segment/required?segmentId=' + segmentId + '&requiredKg=' + requiredKg, {});
        return response;
    }
}
